import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import Theme from 'components/common/Theme/Theme'
import Mixin from 'components/common/Mixin'
import Media from 'components/common/Media'
import Popover from 'components/common/Popover'
import { LazyDiv } from 'components/common/LazyHtml'

const Item = ({
  children,
  className,
  clear,
  components = undefined,
  smallPadding,
  title,
  tooltip
}) =>
  React.Children.count(children) > 1 ?
    <Wrapper classname={ className }>
      <PropertyAsSpan data-at-details-property={ title } number={ React.Children.count(children) } clear={ clear }>
        { title }
        {
          tooltip && <PopoverWrapper>
            <Popover components={ components } small>
              <Content content={ tooltip } />
            </Popover>
          </PopoverWrapper>
        }
      </PropertyAsSpan>
      { React.Children.map(children, (element, index) => (
        <ValueAsSpan
          key={ index }
          data-at-details-value={ title }
          number={ React.Children.count(children) }
          smallPadding={ smallPadding }
          clear={ clear }>
          { element }
        </ValueAsSpan>
      )) }
    </Wrapper> :
    <Fragment>
      <Property data-at-details-property={ title } number={ React.Children.count(children) } clear={ clear }>
        { title }
        {
          tooltip && <PopoverWrapper>
            <Popover components={ components } small>
              <Content content={ tooltip } />
            </Popover>
          </PopoverWrapper> }
      </Property>
      { React.Children.map(children, (element, index) => (
        <Value
          key={ index }
          data-at-details-value={ title }
          number={ React.Children.count(children) }
          smallPadding={ smallPadding }
          clear={ clear }>
          { element }
        </Value >
      )) }
    </Fragment>


Item.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  className: PropTypes.string,
  clear: PropTypes.bool,
  components: PropTypes.shape({}),
  smallPadding: PropTypes.string,
  title: PropTypes.string,
  tooltip: PropTypes.string
}

const Value = styled.dd`
  color: ${Theme.colors.black_new};
  line-height: 22px;
  display: inline-block;
  width: 50%;
  margin-left: 0;
  padding: 13px 0 14px 21px;
  vertical-align: top;
  background: ${Theme.colors.white};
  border: 1px solid ${Theme.colors.light_grey};
  border-width: 0 2px 1px 0;
  ${Media.desktop`
    ${props => props.number === 1 && css`
      width: 50%;
      border-right: 0;
    `}
    ${props => props.number === 2 && css`
      width: 33.3333%;
    `}
    ${props => props.number === 3 && css`
      width: 25%;
    `}
  `}
  ${Media.mobile`
    padding: 13px 11px 14px;
    ${props => props.number === 1 && css`
      width: 100%;
    `}
    ${props => props.number === 2 && css`
      width: 50%;
    `}
    ${props => props.number === 3 && css`
      width: 33.3333%;
    `}

    &:last-child {
      border-right: none;
    }
  `}
  ${props => props.smallPadding && css`
    ${Mixin.responsive('padding', '10px 21px', '10px 16px')}
  `}
  ${props => props.clear && css`
    padding: 5px 0;
    border: none;
  `}
  ${Mixin.font.GothamBook(14)}
`

const Property = styled.dt`
  color: ${Theme.colors.black};
  line-height: 22px;
  display: inline-block;
  width: 50%;
  position: relative;
  text-transform: capitalize;
  vertical-align: top;
  background: ${Theme.colors.off_white};
  border: 1px solid ${Theme.colors.light_grey};
  border-width: 0 2px 1px 0;
  ${Mixin.responsive('padding', '13px 27px 14px 15px', '13px 32px 14px 16px')}
  ${Media.desktop`
    ${props => props.number === 1 && css`
      width: 50%;
      border-right: 0;
    `}
    ${props => props.number === 2 && css`
      width: 33.3333%;
    `}
    ${props => props.number === 3 && css`
      width: 25%;
    `}
  `}
  ${Media.mobile`
    width: 100%;
    border-right: none;

    & + ${Value} {
      padding-left: 16px;
    }
  `}
  ${props => props.clear && css`
    padding: 5px 5px 5px 0;
    border: none;
    background: none;
  `}
  ${Mixin.font.GothamBook(14)}
`
const ValueAsSpan = styled(Value.withComponent('span'))`
  display: table-cell;
`

const PropertyAsSpan = styled(Property.withComponent('span'))`
  display: table-cell;
`

const Content = styled(LazyDiv)`
  margin: 0 0 5px;
  line-height: 18px;
  color: ${Theme.colors.dark_grey};
  ${Mixin.font.GothamBook(12)}
`

const Wrapper = styled.div`
  display: table-row;
  line-height: 22px;
  color: ${Theme.colors.black};
  ${Mixin.font.GothamBook(14)}
  ${Media.mobile`
    display: flex;
    flex-wrap: wrap;
  `}
`

const PopoverWrapper = styled.div`
  position: absolute;
  top: 50%;
  right: 12px;
  margin-top: -8px;
  line-height: 0;
  ${Media.mobile`
    right: 17px;
  `}

  & [data-tooltip] {
    width: 16px;
    height: 16px;
    margin-right: 0;
  }
`

export default Item
